/**
 * SVG metadata embed/extract.
 *
 * Standard SVG metadata:
 *   <title>      — alt text / caption (accessible to screen readers)
 *   <desc>       — longer description
 *   <metadata>   — structured metadata container (Dublin Core + custom)
 *
 * App-specific data is stored in a custom namespaced element:
 *   <alchemie:data xmlns:alchemie="https://alchem.ie/ns/svg">JSON</alchemie:data>
 *
 * Dublin Core fields (author, copyright) use standard RDF:
 *   <dc:creator>, <dc:rights>
 */

import type { ExportMetadata } from './types';
import { encodeAlchemieData, decodeAlchemieData } from './png';

const ALCHEMIE_NS = 'https://alchem.ie/ns/svg';
const DC_NS = 'http://purl.org/dc/elements/1.1/';
const RDF_NS = 'http://www.w3.org/1999/02/22-rdf-syntax-ns#';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Escape XML special characters. */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Unescape XML entities back to plain text. */
function unescapeXml(text: string): string {
  return text
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&');
}

/** Extract text content between a specific XML tag. Returns null if not found. */
function extractTagContent(svg: string, tag: string, namespace?: string): string | null {
  // Try with namespace prefix first, then without
  const patterns: RegExp[] = [];
  if (namespace) {
    // Match any prefix for the given namespace
    const nsEscaped = namespace.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    patterns.push(new RegExp(`<[a-zA-Z]+:${tag}[^>]*xmlns:[a-zA-Z]+="${nsEscaped}"[^>]*>([\\s\\S]*?)</[a-zA-Z]+:${tag}>`, 'i'));
    patterns.push(new RegExp(`<[a-zA-Z]+:${tag}[^>]*>([\\s\\S]*?)</[a-zA-Z]+:${tag}>`, 'i'));
  }
  patterns.push(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)</${tag}>`, 'i'));

  for (const pattern of patterns) {
    const match = svg.match(pattern);
    if (match) return unescapeXml(match[1].trim());
  }
  return null;
}

/** Remove a tag and its content from SVG string. */
function removeTag(svg: string, tag: string): string {
  // Remove both namespaced and non-namespaced versions
  return svg
    .replace(new RegExp(`\\s*<[a-zA-Z]*:?${tag}[^>]*>[\\s\\S]*?</[a-zA-Z]*:?${tag}>\\s*`, 'gi'), '')
    .replace(new RegExp(`\\s*<${tag}[^>]*>[\\s\\S]*?</${tag}>\\s*`, 'gi'), '');
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Embed metadata into an SVG string.
 *
 * Adds/replaces <title>, <desc>, and <metadata> elements inside the root <svg>.
 * Existing elements are replaced; new ones are inserted after the opening <svg> tag.
 *
 * @param svgString - Raw SVG content
 * @param meta - Metadata to embed
 * @returns New SVG string with metadata embedded
 */
export function embedSVGMetadata(svgString: string, meta: ExportMetadata): string {
  let svg = svgString;

  // Remove existing metadata elements we'll replace
  if (meta.title !== undefined) svg = removeTag(svg, 'title');
  if (meta.description !== undefined) svg = removeTag(svg, 'desc');
  // Always remove old metadata block if we have anything to add
  const hasMetadataFields = meta.author || meta.software || meta.copyright || meta.alchemieData;
  if (hasMetadataFields) svg = removeTag(svg, 'metadata');

  // Build new elements to insert
  const elements: string[] = [];

  if (meta.title) {
    elements.push(`<title>${escapeXml(meta.title)}</title>`);
  }

  if (meta.description) {
    elements.push(`<desc>${escapeXml(meta.description)}</desc>`);
  }

  if (hasMetadataFields) {
    const metaParts: string[] = [];

    if (meta.author || meta.copyright) {
      const dcParts: string[] = [];
      if (meta.author) dcParts.push(`      <dc:creator>${escapeXml(meta.author)}</dc:creator>`);
      if (meta.software) dcParts.push(`      <dc:source>${escapeXml(meta.software)}</dc:source>`);
      if (meta.copyright) dcParts.push(`      <dc:rights>${escapeXml(meta.copyright)}</dc:rights>`);

      metaParts.push(
        `  <rdf:RDF xmlns:rdf="${RDF_NS}" xmlns:dc="${DC_NS}">`,
        `    <rdf:Description>`,
        ...dcParts,
        `    </rdf:Description>`,
        `  </rdf:RDF>`
      );
    }

    if (meta.alchemieData) {
      const encoded = encodeAlchemieData(meta.alchemieData);
      metaParts.push(`  <alchemie:data xmlns:alchemie="${ALCHEMIE_NS}">${escapeXml(encoded)}</alchemie:data>`);
    }

    if (metaParts.length > 0) {
      elements.push(`<metadata>\n${metaParts.join('\n')}\n</metadata>`);
    }
  }

  if (elements.length === 0) return svgString;

  // Insert after opening <svg> tag
  const insertion = '\n' + elements.join('\n') + '\n';
  const svgOpenEnd = svg.indexOf('>', svg.indexOf('<svg'));
  if (svgOpenEnd === -1) return svgString; // Malformed SVG

  return svg.slice(0, svgOpenEnd + 1) + insertion + svg.slice(svgOpenEnd + 1);
}

/**
 * Extract metadata from an SVG string.
 *
 * Reads <title>, <desc>, and <metadata> elements.
 * AlchemieData is JSON-decoded automatically.
 *
 * @param svgString - Raw SVG content
 * @returns Extracted metadata (empty fields omitted)
 */
export function extractSVGMetadata(svgString: string): ExportMetadata {
  const meta: ExportMetadata = {};

  const title = extractTagContent(svgString, 'title');
  if (title) meta.title = title;

  const desc = extractTagContent(svgString, 'desc');
  if (desc) meta.description = desc;

  // Dublin Core fields from <metadata>
  const creator = extractTagContent(svgString, 'creator', DC_NS);
  if (creator) meta.author = creator;

  const source = extractTagContent(svgString, 'source', DC_NS);
  if (source) meta.software = source;

  const rights = extractTagContent(svgString, 'rights', DC_NS);
  if (rights) meta.copyright = rights;

  // Alchemie data
  const alchemieRaw = extractTagContent(svgString, 'data', ALCHEMIE_NS);
  if (alchemieRaw) {
    const decoded = decodeAlchemieData(alchemieRaw);
    if (decoded) meta.alchemieData = decoded;
  }

  return meta;
}
